// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { ShareEntityButtonStyles } from "@insite/content-library/Components/ShareEntityButton";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import { IconPresentationProps, IconWrapper } from "@insite/mobius/Icon";
import Mail from "@insite/mobius/Icons/Mail";
import Trash2 from "@insite/mobius/Icons/Trash2";
import { LinkPresentationProps } from "@insite/mobius/Link";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import IconCartReorderLink from "@rpsc/XDL/Icons/IconCartReorderLink";
import Check from "@rpsc/XDL/Icons/IconCheck";
import Plus from "@rpsc/XDL/Icons/IconPlus";
import Print from "@rpsc/XDL/Icons/IconPrint";
import IconSaveOrder from "@rpsc/XDL/Icons/IconSaveOrder";
import { fontConfig } from "@rpsc/XDL/PreStyle/typography";
import { createLinkIconStyles } from "@rpsc/XDL/Utilities";
import { css } from "styled-components";

interface MyAccountDetailsStyles {
    myAccountDetailsActions?: {
        saveOrderLink?: LinkPresentationProps;
        placeOrderLink?: LinkPresentationProps;
        acceptLink?: LinkPresentationProps;
        addAllToListClickable?: LinkPresentationProps;
        removeAllLink?: LinkPresentationProps;
        printLink?: LinkPresentationProps;
        emailLink?: ShareEntityButtonStyles;
        requoteLink?: LinkPresentationProps;
    };
}
const iconStyles: IconPresentationProps = {
    size: 12,
};
const typographyStyles: TypographyPresentationProps = {
    size: fontConfig.fontSize.xSmall,
    lineHeight: "22px",
    weight: "bold",
};

const myAccountDetailsStyles: MyAccountDetailsStyles = {
    myAccountDetailsActions: {
        saveOrderLink: {
            ...createLinkIconStyles(IconSaveOrder, {}, typographyStyles, iconStyles),
        },
        placeOrderLink: {
            ...createLinkIconStyles(IconSaveOrder, {}, typographyStyles, iconStyles),
        },
        acceptLink: {
            ...createLinkIconStyles(Check, {}, typographyStyles, iconStyles),
        },
        addAllToListClickable: {
            ...createLinkIconStyles(Plus, {}, typographyStyles, iconStyles),
        },
        removeAllLink: {
            ...createLinkIconStyles(Trash2, {}, typographyStyles, iconStyles),
        },
        printLink: {
            ...createLinkIconStyles(Print, {}, typographyStyles, {
                size: 16,
                css: css`
                    &,
                    & > svg {
                        height: 17px;
                    }
                `,
            }),
        },
        requoteLink: {
            ...createLinkIconStyles(IconCartReorderLink, {}, typographyStyles, {
                size: 20,
                css: css`
                    &,
                    & > svg {
                        height: 17px;
                    }
                `,
            }),
        },
        emailLink: {
            button: {
                variant: "tertiary",
                color: "text.link",
                icon: {
                    src: Mail,
                    position: "left",
                },
                typographyProps: {
                    ...typographyStyles,
                },
                css: css`
                    border: none;
                    padding: 0;
                    min-height: auto;
                    ${IconWrapper}, svg {
                        width: 16px;
                        height: 17px;
                        top: 0;
                    }
                `,
            },
        },
    },
};

export const linkLikeButtonCss: any = {
    icon: {
        position: "left",
    },
    typographyProps: {
        size: 12,
        lineHeight: "22px",
        weight: "bold",
    },
    css: css`
        padding: 0;
        margin: 0;
        transition: none;
        border: none;
        min-height: auto;
        ${IconWrapper}, ${IconWrapper} svg {
            width: 12px;
            height: 12px;
        }
        &&&:hover,
        &&&:focus {
            border: none;
            outline: none;
        }
    `,
};

export const createListDetailButtonStyle = (iconSrc?: React.ComponentType | string) => {
    return {
        variant: "tertiary",
        icon: {
            src: iconSrc,
            position: "right",
        },
        css: css`
            padding: 0 15px;
            margin-right: 10px;
            border-color: transparent;
            ${IconWrapper} {
                margin-bottom: 5px;
            }
        `,
    } as ButtonPresentationProps;
};

export default myAccountDetailsStyles;
